import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import startCase from 'lodash/startCase'
import Layout from 'containers/Layout'
import moment from 'moment'
import _, { get, orderBy } from 'lodash'
import apiGet from 'lib/network/apiGet'
import apiGetBlob from 'lib/network/apiGetBlob'
import apiPost from 'lib/network/apiPost'
import notify from 'components/atoms/Notify'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { UserIcon } from '@heroicons/react/outline'

import Selection from 'components/molecules/Selection'
import Table from 'components/pageComponents/Table'
import BookedUsersCards from 'components/pageComponents/BookedUsersCards'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import { accessibleOnClick } from 'utils'
import { useSelector } from 'react-redux'
import classNames from 'classnames'

import useModal from 'hooks/useModal'

import AddUser from './modals/AddUserModal'
import { IoSearch } from 'react-icons/io5'
import { setUser } from 'state/redux/actions/user.actions'
import Switch from 'components/atoms/Switch'
import PageFooter from 'components/paginationComponent'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'

const Users = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const location = useLocation()

  // Check if current language is RTL (Arabic)
  const isRTL = i18n.language === 'ar'

  // Helper to parse query params
  const getQueryParams = () => {
    const params = new URLSearchParams(location.search)
    return {
      company: params.get('company') || undefined,
      role: params.get('role') || undefined,
      softDeletedUsers: params.get('softDeletedUsers') === 'true',
      pendingDocuments: params.get('pendingDocuments') === 'true',
      search: params.get('search') || '',
    }
  }

  // Initialize filter/search from query params
  const initialParams = getQueryParams()
  const [filter, setFilter] = useState({
    type: null,
    workspace: null,
    company: initialParams.company,
    role: initialParams.role,
    softDeletedUsers: initialParams.softDeletedUsers,
    pendingDocuments: initialParams.pendingDocuments,
  })
  const userFromStore = useSelector(state => get(state, 'user.user'))
  const companyBranchFilterId = useSelector(state => get(state, 'settings.companyBranchFilterId'))
  // eslint-disable-next-line
  const isCompanyRole = userFromStore?.roles?.some(role => role.type === 'company')
  // eslint-disable-next-line
  const hasAdminAccess = userFromStore?.roles?.some(role => role.type === 'admin')
  const isSuperCompanyUser = userFromStore?.superCompanyUser
  const companyReportPadding = isCompanyRole && !hasAdminAccess

  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [pagination, setPagination] = useState({})
  const [users, setUsers] = useState([])
  const [search, setSearch] = useState(initialParams.search)
  const [companyNames, setCompanyNames] = useState([])
  const [role, setRole] = useState()
  const [isBookedUsersView, setIsBookedUsersView] = useState(false)
  const [generatingWalletBalanceReport, setGeneratingWalletBalanceReport] = useState(false)
  const scrollPositionRef = useRef(0)

  const { id } = useParams()

  const navigateToUserDetail = userId => {
    const searchParams = new URLSearchParams(location.search)
    const queryString = searchParams.toString()
    const userDetailPath = queryString ? `/users/edit/${userId}?${queryString}` : `/users/edit/${userId}`
    history.push(userDetailPath)
  }

  const updateQueryParams = (newFilter, newSearch) => {
    const params = new URLSearchParams()
    if (newFilter.company) params.set('company', newFilter.company)
    if (newFilter.role) params.set('role', newFilter.role)
    if (newFilter.softDeletedUsers) params.set('softDeletedUsers', 'true')
    if (newFilter.pendingDocuments) params.set('pendingDocuments', 'true')
    if (newSearch) params.set('search', newSearch)
    history.replace({ pathname: '/users', search: params.toString() })
  }

  useEffect(() => {
    updateQueryParams(filter, search)
    // eslint-disable-next-line
  }, [filter, search])

  useEffect(() => {
    fetchUsers()
  }, [filter, search, companyBranchFilterId])

  const fetchUsers = async (page = 1, role = null, company = null) => {
    const isInitialPage = page === 1
    if (isInitialPage) {
      setLoading(true)
    } else {
      setLoadingMore(true)
    }

    try {
      const params = {}
      params.pageNumber = page
      params.pageSize = 20
      params.query = search || undefined

      if (filter.role || role) params.roleId = role || filter.role

      if (filter.company || company) params.companyId = company || filter.company
      if (filter.softDeletedUsers) params.softDeletedUsers = filter?.softDeletedUsers
      if (filter.pendingDocuments) params.pendingDocuments = filter?.pendingDocuments
      if (filter.sortBy && filter.sortOrder) {
        params.sortBy = filter.sortBy
        params.sortOrder = filter.sortOrder
      }

      let endpoint = 'users'
      if ((isCompanyRole && !hasAdminAccess) || (isSuperCompanyUser && !hasAdminAccess)) {
        endpoint = 'branch/booked-users'
        setIsBookedUsersView(true)
      } else {
        setIsBookedUsersView(false)
      }

      const response = await apiGet(endpoint, params)

      const list = response?.data?.user || []
      setUsers(prev => (isInitialPage ? list : [...prev, ...list]))
      setPagination(response?.data?.pagination || [])
    } finally {
      if (isInitialPage) {
        setLoading(false)
      } else {
        setLoadingMore(false)
      }
    }
  }

  useEffect(() => {
    fetchCompanies()
    if (hasAdminAccess) {
      fetchRole()
    }
  }, [hasAdminAccess])

  const fetchCompanies = async () => {
    const response = await apiGet('companies', { pageSize: 1000 })
    if (response?.data) {
      const companies = response.data.company.map(company => ({
        id: company._id,
        name: company.name,
      }))
      setCompanyNames(companies)
    } else {
      setCompanyNames([])
    }
  }

  const fetchRole = async () => {
    const response = await apiGet('roles')
    if (response?.data) {
      const roles = _.map(response.data, role => ({
        id: role._id,
        name: role.name,
      }))
      setRole(roles)
    } else {
      setRole([])
    }
  }

  const handleUserTypeChange = selectedOption => {
    setFilter(prevFilter => ({
      ...prevFilter,
      role: prevFilter.role === selectedOption?.id ? null : selectedOption?.id,
    }))
  }

  const handleCompanyTypeChange = selectedOption => {
    setFilter(prevFilter => ({
      ...prevFilter,
      company: prevFilter.company === selectedOption?.id ? null : selectedOption?.id,
    }))
  }

  const handleImpersonation = async userId => {
    try {
      const response = await apiPost(`impersonation/${userId}`, {})

      if (response.success && response.data?.token) {
        const token = response.data.token
        const redirectUrl = `${process.env.REACT_APP_FRONTEND_HOST_URL}?impersonation_token=${encodeURIComponent(token)}`
        window.open(redirectUrl, '_blank')
      }
    } catch (error) {
      console.error('Impersonation failed:', error)
    }
  }
  const user = useSelector(state => get(state, 'user.user'))

  const handleAddUserSuccess = userId => {
    closeModal()
    navigateToUserDetail(userId)
    fetchUsers()
  }
  const hasUserRole = item => {
    // eslint-disable-next-line
    return item?.roles?.some(role => role.type === 'user')
  }

  const [AddUserModal, openAddUserModel, closeModal] = useModal({
    content: <AddUser onSuccess={handleAddUserSuccess} />,
    title: t('users.modal.addUser'),
    // dimensions: {
    //   height: '250px',
    //   width: '450px',
    // },
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  const handleGenerateWalletBalanceReport = async () => {
    setGeneratingWalletBalanceReport(true)
    try {
      const blob = await apiGetBlob('wallet-balance-report')
      if (!blob) {
        setGeneratingWalletBalanceReport(false)
        return
      }
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `wallet-balance-report-${moment().format('YYYY-MM-DD')}.xlsx`
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
      notify.success('Wallet Balance Report', 'Report downloaded successfully')
    } catch (error) {
      notify.error('Wallet Balance Report', error?.message || 'Failed to download report')
    } finally {
      setGeneratingWalletBalanceReport(false)
    }
  }

  const columns = [
    {
      name: t('users.columns.name'),
      width: '10%',
      key: 'name',
      sortable: true,
      rendered: item => {
        return (
          <div className="flex items-center cursor-pointer" {...accessibleOnClick(() => navigateToUserDetail(item._id))}>
            <div className="flex-shrink-0 h-10 w-10">
              {item?.avatar ? (
                <img className="h-10 w-10 rounded-full object-cover" src={item?.avatar} alt="" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center shrink-0" aria-hidden>
                  <UserIcon className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                </div>
              )}
            </div>
            <div className={isRTL ? 'mr-4' : 'ml-4'}>
              <div className="text-sm text-normal text-gray-900">{item?.fullName || 'User'}</div>
            </div>
          </div>
        )
      },
    },
    {
      width: '10%',
      name: t('users.columns.email'),
      key: 'email',
      sortable: true,
      rendered: item => {
        const userEmail = item.email

        return (
          <>
            <div>
              <span className="text-[13px]">{userEmail}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('users.columns.phone'),
      key: 'phone',
      sortable: true,
      rendered: item => {
        const userPhone = item.phone
        return (
          <>
            <div>
              <span className="text-[13px]">{userPhone}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('users.columns.type'),
      key: 'type_wa',
      sortable: true,
      rendered: item => (
        <span className="text-[13px]">
          {
            // eslint-disable-next-line
            orderBy(item?.roles?.filter(d => d.name !== 'platform_admin')?.map(r => startCase(r.name)))?.map(d => (
              <span className="border mr-3 shadow-sm rounded-2xl px-2 py-0.5" key={d}>
                {d}
              </span>
            ))
          }
        </span>
      ),
    },
    {
      width: '5%',
      name: t('users.columns.companyName'),
      key: 'company',
      sortable: true,
      rendered: item => {
        if (item.company && item.company.length > 0) {
          return <span className=" text-[13px]">{item.company[0].name}</span>
        }
      },
    },
    {
      width: '10%',
      name: t('users.columns.branch'),
      key: 'branch',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">
                {(item?.branch || [])
                  .map(b => b?.name)
                  .filter(Boolean)
                  .join(', ')}
              </span>
            </div>
          </>
        )
      },
    },
    {
      width: '5%',
      name: t('users.columns.created'),
      key: 'created',
      sortable: true,
      rendered: item => {
        const createdTime = get(item, 'createdAt', '')
        const createdAt = createdTime ? moment(createdTime).fromNow() : 'Registered'
        return createdAt
      },
    },
    {
      width: '5%',
      name: t('users.columns.lastActivity'),
      key: 'lastActivity',
      sortable: true,
      rendered: item => {
        // Stamped on any authenticated request (throttled), so a user who has
        // never been seen since the field was added simply has none.
        const lastActivity = get(item, 'lastActivity', '')
        return <span className="text-[13px]">{lastActivity ? moment(lastActivity).fromNow() : '-'}</span>
      },
    },
    {
      name: t('users.columns.action'),
      width: '5%',
      key: 'completedAt',
      align: 'left',
      rendered: item => (
        <>
          <div className="flex space-x-4">
            {hasUserRole(item) && (
              <button
                onClick={() => handleImpersonation(item._id)}
                className="w-full flex justify-center py-1 px-1 border border-transparent rounded-md shadow-sm text-sm text-normal text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
              >
                {t('users.buttons.impersonate')}
              </button>
            )}
          </div>
        </>
      ),
    },
  ]

  return (
    <Layout
      showLogo={!!id}
      breadcrumbs={[{ name: t('users.breadcrumb'), pathname: '/users' }]}
      headerRight={
        hasAdminAccess && (
          <div className="flex flex-col sm:flex-row items-center justify-end gap-3 sm:gap-4 w-full min-w-0">
            {/* Switches */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 shrink-0">
              <div className="flex items-center gap-3">
                <p className="whitespace-nowrap text-sm">{t('users.filters.softDeleted')}</p>
                <Switch
                  value={filter.softDeletedUsers}
                  onChange={e => {
                    setFilter(prevFilter => ({
                      ...prevFilter,
                      softDeletedUsers: e,
                    }))
                  }}
                />
              </div>
              <div className="flex items-center gap-3">
                <p className="whitespace-nowrap text-sm">{t('users.filters.documentVerificationRequired')}</p>
                <Switch
                  value={filter.pendingDocuments}
                  onChange={e => {
                    setFilter(prevFilter => ({
                      ...prevFilter,
                      pendingDocuments: e,
                    }))
                  }}
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 shrink-0">
              <Selection
                value={filter.company}
                onChange={e => {
                  setFilter(prevFilter => ({
                    ...prevFilter,
                    company: prevFilter.company === e?.id ? null : e?.id,
                  }))
                }}
                className="min-w-[180px] w-full sm:w-auto"
                list={companyNames}
                title={t('users.filters.companyFilter')}
              />
              <Selection
                value={filter.role}
                onChange={e => {
                  setUsers([])
                  handleUserTypeChange(e)
                }}
                className="min-w-[180px] w-full sm:w-auto"
                list={role}
                title={t('users.filters.userFilter')}
              />
            </div>

            {/* Search */}
            <div className="flex items-center gap-3 shrink-0">
              <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
              <div className="relative rounded-md shadow-sm w-40">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                  <IoSearch className="h-4 w-4 text-gray-400" aria-hidden="true" />
                </div>
                <input
                  onChange={e => setSearch(e.target.value)}
                  value={search}
                  type="text"
                  name="search"
                  id="search"
                  className="block w-full pl-9 shadow-none sm:text-sm bg-gray-100 border-0 focus:outline-none rounded-sm"
                  placeholder={t('users.filters.searchPlaceholder')}
                />
              </div>
            </div>

            {/* Actions — kept as a separate group so buttons never overlap search */}
            <div className="flex items-center gap-3 shrink-0 pl-2 border-l border-gray-200">
              <button
                type="button"
                onClick={handleGenerateWalletBalanceReport}
                disabled={generatingWalletBalanceReport}
                className="whitespace-nowrap h-[30px] inline-flex items-center px-4 py-1 border border-transparent shadow-sm text-xs rounded-md text-white bg-dark-600 hover:bg-dark-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {generatingWalletBalanceReport ? '...' : 'Wallet Balance Report'}
              </button>
              <button
                type="button"
                onClick={openAddUserModel}
                className="whitespace-nowrap h-[30px] inline-flex items-center px-4 py-1 border border-transparent shadow-sm text-xs rounded-md text-white bg-dark-600 hover:bg-dark-700"
              >
                {t('users.buttons.addUser')}
              </button>
            </div>
          </div>
        )
      }
      title={t('users.title')}
    >
      <div className="flex flex-col">
        <div className="flex-1 flex items-stretch">
          <main className={classNames('flex-1', companyReportPadding && 'px-4 pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-6 lg:px-8 lg:pb-8 lg:pt-8')}>
            <div className="align-middle inline-block min-w-full">
              <div className="min-w-full">
                {!loading ? (
                  <>
                    {users?.length === 0 ? (
                      <EmptyState v2 description={t('users.emptyState.description')} />
                    ) : isBookedUsersView ? (
                      <BookedUsersCards
                        users={users}
                        loadingMore={loadingMore}
                        hasMore={pagination?.currentPage !== pagination?.totalPages}
                        fetchData={() => fetchUsers(pagination?.currentPage + 1)}
                      />
                    ) : (
                      <Table
                        hasMore={pagination?.currentPage !== pagination?.totalPages}
                        fetchData={() => fetchUsers(pagination?.currentPage + 1)}
                        sortBy={filter.sortBy}
                        sortOrder={filter.sortOrder}
                        handleSort={(sortBy, sortOrder) => {
                          setFilter(prevFilter => ({ ...prevFilter, sortBy, sortOrder }))
                        }}
                        data={users}
                        columns={columns}
                      />
                    )}
                  </>
                ) : (
                  <div className="w-full h-[200px] border-0 flex justify-center items-center">
                    <LoadingIcon />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
      <AddUserModal />
    </Layout>
  )
}

export default Users
